import type { CapacityLine } from '../../utils/dashboard';
import { formatMinutes } from '../../utils/dashboard';
import { dashboardCopy } from '../../copy/dashboard';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// CapacityStrip — one dense line, no chart. Three numbers and a proportional
// bar so the shape of the day reads at a glance. If the day is overloaded
// the bar fills its full width and the RiskStrip carries the severity.
// ---------------------------------------------------------------------------

interface CapacityStripProps {
  capacity: CapacityLine | null;
  loading?: boolean;
}

export function CapacityStrip({ capacity, loading = false }: CapacityStripProps) {
  if (loading) {
    return <div className="h-14 animate-pulse rounded-2xl bg-card/60" />;
  }
  if (!capacity) return null;

  const total = capacity.committedMinutes + capacity.protectedMinutes + capacity.slackMinutes;
  const safeTotal = total > 0 ? total : 1; // Avoid div-by-zero in rare empty plans.

  const segments = [
    {
      key: 'committed',
      label: dashboardCopy.capacityCommitted,
      value: capacity.committedMinutes,
      barClass: 'bg-primary',
    },
    {
      key: 'protected',
      label: dashboardCopy.capacityProtected,
      value: capacity.protectedMinutes,
      barClass: 'bg-emerald-500',
    },
    {
      key: 'slack',
      label: dashboardCopy.capacitySlack,
      value: capacity.slackMinutes,
      barClass: 'bg-border',
    },
  ];

  return (
    // No wrapper card — the parent CardContent in DashboardPage owns
    // the chrome. `h-full + flex flex-col` lets the bar push to the
    // bottom via `mt-auto` so the bars across the three KPI cards
    // (capacity / focus protected / strategic alignment) all align at
    // the same Y position. Without this, Card 1's bar floated higher
    // because the segmented label row is shorter than the 26pt mono
    // headline numbers in the other two cards.
    <div className="flex h-full flex-col">
      {/* Numeric summary — the primary information. */}
      <div className="flex flex-wrap items-baseline gap-5">
        {segments.map((seg) => (
          <div key={seg.key} className="flex items-baseline gap-1.5">
            <span className="text-sm font-bold tabular-nums text-foreground">
              {formatMinutes(seg.value)}
            </span>
            <span className="text-xs text-muted-foreground">{seg.label}</span>
          </div>
        ))}
      </div>

      {/* Proportional bar — visual rhythm, not an axis to read exactly. */}
      <div className="mt-auto flex h-1.5 overflow-hidden rounded-full bg-muted/60">
        {segments.map((seg) => (
          <div
            key={seg.key}
            aria-hidden
            className={cn('transition-[width] duration-200', seg.barClass)}
            style={{ width: `${(seg.value / safeTotal) * 100}%` }}
          />
        ))}
      </div>
    </div>
  );
}

export default CapacityStrip;
